/**
 * useLearningTracking - Hook para tracking del sistema de aprendizaje de ARES
 * 
 * Este hook maneja:
 * - Feedback implícito (tiempo de lectura, clicks, exports)
 * - Feedback explícito (thumbs up/down)
 * - Eventos de interacción con el mapa
 * - Autocomplete basado en patrones previos
 */

import { useCallback, useRef, useEffect } from 'react';
import { useAuthStore } from '@/stores/authStore';
import { API_BASE_URL } from '@/utils/constants';

// Types
export type FeedbackType = 
  | 'implicit_long_dwell'
  | 'implicit_quick_close'
  | 'implicit_map_click'
  | 'implicit_export'
  | 'implicit_follow_up'
  | 'explicit_thumbs_up'
  | 'explicit_thumbs_down'
  | 'explicit_correction';

export interface LearningEvent {
  session_id: string;
  query: string;
  response_summary: string;
  feedback_type: FeedbackType;
  metadata?: Record<string, unknown>;
}

export interface LearningSummary {
  synonyms_learned: number;
  verified_synonyms: Array<{
    term: string;
    canonical: string;
    context: string;
    confidence: number;
  }>;
  context_patterns: number;
  preferences: {
    common_filters: Record<string, unknown>;
    favorite_format: string | null;
    typical_time_range: string | null;
  };
}

/**
 * Hook principal para tracking de aprendizaje
 */
export function useLearningTracking() {
  const getSessionId = useAuthStore((s) => s.getSessionId);
  const token = useAuthStore((s) => s.token);

  // Track dwell time
  const dwellStartRef = useRef<number | null>(null);
  const currentQueryRef = useRef<string>('');

  /**
   * Enviar tracking event al backend
   */
  const sendTrackingEvent = useCallback(async (
    endpoint: string,
    data: Record<string, unknown>
  ): Promise<boolean> => {
    try {
      if (!token) {
        console.debug('[Learning] No auth token, skipping tracking');
        return false;
      }

      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        console.warn('[Learning] Tracking failed:', response.status);
        return false;
      }

      return true;
    } catch (error) {
      console.error('[Learning] Tracking error:', error);
      return false;
    }
  }, [token]);

  /**
   * Track implicit feedback
   */
  const trackImplicitFeedback = useCallback((
    query: string,
    responseSummary: string,
    feedbackType: FeedbackType,
    metadata?: Record<string, unknown>
  ): void => {
    const sessionId = getSessionId();
    
    sendTrackingEvent('/api/learning/feedback/implicit', {
      session_id: sessionId,
      query,
      response_summary: responseSummary,
      feedback_type: feedbackType,
      metadata: metadata || {},
    });
  }, [getSessionId, sendTrackingEvent]);

  /**
   * Track explicit feedback (thumbs up/down)
   */
  const trackExplicitFeedback = useCallback((
    query: string,
    responseSummary: string,
    isPositive: boolean,
    correction?: string
  ): void => {
    const sessionId = getSessionId();
    
    sendTrackingEvent('/api/learning/feedback/explicit', {
      session_id: sessionId,
      query,
      response_summary: responseSummary,
      is_positive: isPositive,
      correction: correction || null,
    });
  }, [getSessionId, sendTrackingEvent]);

  /**
   * Start tracking dwell time for a query
   */
  const startDwellTracking = useCallback((query: string): void => {
    dwellStartRef.current = Date.now();
    currentQueryRef.current = query;
  }, []);

  /**
   * End tracking dwell time and report if significant
   */
  const endDwellTracking = useCallback((responseSummary: string = ''): void => {
    if (dwellStartRef.current === null) return;

    const dwellTimeMs = Date.now() - dwellStartRef.current;
    const query = currentQueryRef.current;

    // Only track if user spent meaningful time
    if (dwellTimeMs > 10000) {
      // > 10s = implicit positive
      trackImplicitFeedback(
        query,
        responseSummary,
        'implicit_long_dwell',
        { dwell_time_ms: dwellTimeMs }
      );
    } else if (dwellTimeMs < 3000) {
      // < 3s = implicit negative
      trackImplicitFeedback(
        query,
        responseSummary,
        'implicit_quick_close',
        { dwell_time_ms: dwellTimeMs }
      );
    }

    // Reset
    dwellStartRef.current = null;
    currentQueryRef.current = '';
  }, [trackImplicitFeedback]);

  /**
   * Reset dwell tracking refs without reporting
   * Called when user gives explicit feedback to cancel implicit tracking
   */
  const resetDwellTracking = useCallback((): void => {
    dwellStartRef.current = null;
    currentQueryRef.current = '';
  }, []);

  /**
   * Track map interaction
   */
  const trackMapInteraction = useCallback((
    query: string,
    interactionType: 'click' | 'zoom' | 'pan',
    markerCount: number = 0
  ): void => {
    if (interactionType === 'click') {
      trackImplicitFeedback(
        query,
        `Map interaction (${markerCount} markers)`,
        'implicit_map_click',
        { marker_count: markerCount }
      );
    }
  }, [trackImplicitFeedback]);

  /**
   * Track export event
   */
  const trackExport = useCallback((
    query: string,
    recordCount: number,
    format: string = 'excel'
  ): void => {
    trackImplicitFeedback(
      query,
      `Exported ${recordCount} records to ${format}`,
      'implicit_export',
      { format, record_count: recordCount }
    );
  }, [trackImplicitFeedback]);

  /**
   * Get query suggestions based on partial input
   */
  const getSuggestions = useCallback(async (
    partialQuery: string,
    maxSuggestions: number = 3
  ): Promise<string[]> => {
    try {
      if (!token || partialQuery.length < 3) {
        return [];
      }

      const response = await fetch(`${API_BASE_URL}/api/learning/suggestions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          partial_query: partialQuery,
          max_suggestions: maxSuggestions,
        }),
      });

      if (!response.ok) return [];

      const data = await response.json();
      return data.suggestions || [];
    } catch (error) {
      console.error('[Learning] Suggestions error:', error);
      return [];
    }
  }, [token]);

  /**
   * Get learning summary for current user
   */
  const getLearningSummary = useCallback(async (): Promise<LearningSummary | null> => {
    try {
      if (!token) return null;

      const response = await fetch(`${API_BASE_URL}/api/learning/summary`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) return null;

      return await response.json();
    } catch (error) {
      console.error('[Learning] Summary error:', error);
      return null;
    }
  }, [token]);

  /**
   * Check if system should ask for clarification
   */
  const shouldClarify = useCallback(async (
    intent: string,
    confidence: number,
    query: string
  ): Promise<{ should_clarify: boolean; reason: string }> => {
    try {
      if (!token) return { should_clarify: false, reason: '' };

      const response = await fetch(`${API_BASE_URL}/api/learning/should-clarify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ intent, confidence, query }),
      });

      if (!response.ok) return { should_clarify: false, reason: '' };

      const data = await response.json();
      return {
        should_clarify: data.should_clarify,
        reason: data.reason,
      };
    } catch (error) {
      console.error('[Learning] Clarify check error:', error);
      return { should_clarify: false, reason: '' };
    }
  }, [token]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Report any pending dwell time on unmount
      if (dwellStartRef.current !== null) {
        endDwellTracking();
      }
    };
  }, [endDwellTracking]);

  return {
    // Core tracking
    trackImplicitFeedback,
    trackExplicitFeedback,
    
    // Specific events
    startDwellTracking,
    endDwellTracking,
    resetDwellTracking,
    trackMapInteraction,
    trackExport,
    
    // Suggestions & queries
    getSuggestions,
    getLearningSummary,
    shouldClarify,
  };
}

/**
 * Hook para tracking de dwell time automático
 * Usar en componentes que muestran respuestas
 */
export function useDwellTracking(query: string, isVisible: boolean) {
  const { startDwellTracking, endDwellTracking } = useLearningTracking();

  useEffect(() => {
    if (isVisible && query) {
      startDwellTracking(query);
    }

    return () => {
      if (isVisible) {
        endDwellTracking();
      }
    };
  }, [isVisible, query, startDwellTracking, endDwellTracking]);
}

export default useLearningTracking;
